import * as React from 'react';
import { Button } from './Button';
import type { ButtonProps } from './Button';
import { icons } from '../icons';
import { t } from '@lib/i18n';

/**
 * 统一功能按钮：图标 + 文字
 *
 * 背景：此前各处按钮形态混乱——纯文字链接、纯图标、图标+文字并存，
 * 尺寸（small / middle / 未指定）与配色（link / text / default / primary / danger）
 * 也各不相同。
 *
 * 规范（全站统一，ssh 页面除外）：
 *   - 一律「图标 + 文字」；图标按 action 从映射表取，同类操作必然同图标
 *   - 尺寸统一 small
 *   - 一律无边框：常规操作用 default（透明底 ghost），页面主操作用 primary（实心）
 *   - delete 等危险操作自动带 danger（红字，同样无边框）
 *
 * 用法：
 *   <ActionButton action="edit" onClick={fn} />
 *   <ActionButton action="delete" onClick={fn} />
 *   <ActionButton action="add" variant="primary" onClick={fn} />
 *   <ActionButton action="copy" onClick={fn}>{t('xxx.copyInstall')}</ActionButton>
 */

export type ActionKind =
  | 'add'
  | 'create'
  | 'edit'
  | 'delete'
  | 'copy'
  | 'show'
  | 'hide'
  | 'refresh'
  | 'reset'
  | 'search'
  | 'save'
  | 'confirm'
  | 'cancel'
  | 'close'
  | 'send'
  | 'reply'
  | 'download'
  | 'more'
  | 'markRead'
  | 'attachment'
  | 'terminal'
  | 'desktop'
  | 'key'
  | 'regenerate'
  | 'statistics'
  | 'ssh'
  | 'rdp'
  | 'password'
  | 'prev'
  | 'next'
  | 'page';

/**
 * action -> 图标名（图标来自 icons 聚合对象，避免解构式命名导出在 IIFE 下的绑定问题）
 *
 * Partial：允许某个 action 不带图标。`page`（分页页码）只显示数字，
 * 配图标反而干扰阅读，故故意不在表中列出。
 */
const ACTION_ICON: Partial<Record<ActionKind, string>> = {
  add: 'PlusOutlined',
  create: 'PlusOutlined',
  edit: 'EditOutlined',
  delete: 'DeleteOutlined',
  copy: 'CopyOutlined',
  show: 'EyeOutlined',
  hide: 'EyeInvisibleOutlined',
  refresh: 'ReloadOutlined',
  reset: 'ReloadOutlined',
  search: 'SearchOutlined',
  save: 'CheckOutlined',
  confirm: 'CheckOutlined',
  cancel: 'CloseCircleOutlined',
  close: 'CloseCircleOutlined',
  send: 'SendOutlined',
  reply: 'SendOutlined',
  download: 'DownloadOutlined',
  more: 'MoreOutlined',
  markRead: 'CheckOutlined',
  attachment: 'PaperClipOutlined',
  terminal: 'CodeOutlined',
  desktop: 'LaptopOutlined',
  key: 'KeyOutlined',
  regenerate: 'SwapOutlined',
  statistics: 'LineChartOutlined',
  ssh: 'CodeOutlined',
  rdp: 'LaptopOutlined',
  password: 'LockOutlined',
  prev: 'LeftOutlined',
  next: 'RightOutlined',
  // page 不设图标：页码只显示数字
};

/** action -> 默认文案的 i18n key；未列出的 action 需由调用方传入文字 */
const ACTION_LABEL: Partial<Record<ActionKind, string>> = {
  add: 'common.add',
  create: 'common.create',
  edit: 'common.edit',
  delete: 'common.delete',
  copy: 'common.copy',
  show: 'common.show',
  hide: 'common.hide',
  refresh: 'common.refresh',
  reset: 'common.reset',
  search: 'common.search',
  save: 'common.save',
  confirm: 'common.confirm',
  cancel: 'common.cancel',
  close: 'common.close',
};

const DANGER_ACTIONS: ReadonlySet<ActionKind> = new Set<ActionKind>(['delete']);

const iconMap = icons as unknown as Record<string, React.FC<{ size?: string | number }>>;

export interface ActionButtonProps
  extends Omit<ButtonProps, 'icon' | 'type' | 'size' | 'children'> {
  action: ActionKind;
  /** 按钮文字；省略时按 action 取默认文案 */
  children?: React.ReactNode;
  /** default 透明无边框（表格内/工具栏） | primary 实心（页面主操作） */
  variant?: 'default' | 'primary';
  /** 窄空间下仅显示图标（如移动端工具栏） */
  iconOnly?: boolean;
}

export const ActionButton: React.FC<ActionButtonProps> = function ActionButton(props) {
  const {
    action,
    children,
    variant = 'default',
    iconOnly = false,
    danger,
    className = '',
    ...rest
  } = props;

  // action 未在 ACTION_ICON 中列出时不渲染图标（如 page 页码）
  const iconName = ACTION_ICON[action];
  const Icon = iconName ? iconMap[iconName] : undefined;
  const labelKey = ACTION_LABEL[action];
  const text = children ?? (labelKey ? t(labelKey) : undefined);

  return (
    <Button
      {...rest}
      // pidoc-action-btn 是给测试用的稳定标记（见 e2e/button-style.spec.ts）。
      // 刻意不使用 h-8 等样式类做选择器：样式类会随视觉调整而变，
      // 一旦变化选择器就选不中，回归用例会静默空转而失去防护意义。
      //
      // 高度统一为 h-8(32px)，不交给内容计算：
      // 工具栏按钮落在 Space(flex) 内被 blockify，实测 30px；
      // 表格行内按钮父级是 Dropdown 的 inline-block 包裹层，实测 46px
      // （content 被撑到 32px，调整 display/align-items/vertical-align 均无效）。
      // 显式高度可绕开两种环境下的高度差异，保证全站等高。
      // self-start 用于避免被 flex 父级 stretch 拉伸（对 inline-block 父级无效，
      // 但保留它可让工具栏等 flex 场景不被邻居撑高）。
      //
      // whitespace-nowrap + 文字 truncate：窄列（如设备页「操作」列）会把按钮
      // 压到比文字自然宽度更窄，此时默认行为是文字换行成竖排（"操/作/▼"），
      // 视觉上是破的。改为不换行 + 超出显示省略号，宁可截断也不变形。
      // min-w-0 是必需的：按钮是 flex 容器，子项的 truncate 需要它允许收缩。
      className={`pidoc-action-btn h-8 self-start whitespace-nowrap ${className}`}
      type={variant === 'primary' ? 'primary' : 'ghost'}
      size="small"
      icon={Icon ? <Icon size="1em" /> : undefined}
      danger={danger ?? DANGER_ACTIONS.has(action)}
    >
      {iconOnly ? undefined : <span className="truncate min-w-0">{text}</span>}
    </Button>
  );
};
